import React, { useState, useRef } from "react";
import {
    IonModal,
    IonButton,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonContent,
    IonLabel,
    IonIcon,
    IonSelect,
    IonSelectOption,
    IonCheckbox,
    IonItem,
    IonToast,
    IonText,
    IonBackdrop,
} from "@ionic/react";
import CustomItem from "./CustomItem";
import SignaturePad, { SignaturePadHandle } from "./SignaturePad";
import { swapHorizontal, call } from "ionicons/icons";
import { User } from "../models/User";
import { Guid } from "guid-typescript";
import { PermitAuthorisationSignature } from "../models/PermitAuthorisationSignature";
import { PermitAssignment } from "../models/PermitAssignment";
import { Role, getPermitRoleSettings } from "../models/Role";

interface Props {
    assignment: PermitAssignment;
    role?: Role;
    permitTypeId?: string;
    currentUser: User | null;
    onSubmit: (signature: PermitAuthorisationSignature, signatureDataUrl: string) => void;
    availableUsers: User[];
    abilityToSign: boolean;
    onRefreshButtonClicked: () => void;
    shouldShowGeofencingError: boolean;
    geofencingError: string | null;
}

const ChangePermitAssigneeModal: React.FC<Props> = ({
    assignment,
    role,
    permitTypeId,
    currentUser,
    onSubmit,
    availableUsers,
    abilityToSign,
    onRefreshButtonClicked,
    shouldShowGeofencingError,
    geofencingError,
}) => {
    const sigPadRef = useRef<SignaturePadHandle>(null);
    const signPadWrapperRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const [selectedUser, setSelectedUser] = useState<User>();
    const [declarationCheckbox, setDeclarationCheckbox] = useState(false);
    const [showError, setShowError] = useState(false);

    const handleCancel = () => {
        setSelectedUser(undefined);
        setDeclarationCheckbox(false);
        sigPadRef.current?.clear();
        setIsOpen(false);
    };

    const beforeDone = () => {
        if (!selectedUser || !declarationCheckbox) {
            setShowError(true);
            setTimeout(() => setShowError(false), 5000);
            return false;
        }
        return true;
    };

    const handleDone = (dataUrl: string) => {
        if (!selectedUser) return;

        const signature = {
            id: Guid.create().toString(),
            roleId: assignment.roleId,
            userId: selectedUser.id,
            declaration: role ? getPermitRoleSettings(role, permitTypeId).authorizationDeclaration : undefined,
            dateSigned: new Date(),
            loggedInUserId: currentUser?.id,
            signatureFileKey: "",
            signatory: {
                name: selectedUser.firstName + " " + selectedUser.lastName,
                roleName: role?.name,
                phoneNumber: selectedUser.telephoneNumber,
            },
        } as PermitAuthorisationSignature;

        onSubmit(signature, dataUrl);
        handleCancel();
    };

    const handelDeclarationCheckbox = (e: CustomEvent) => {
        signPadWrapperRef.current?.setAttribute("tabindex", "0");
        signPadWrapperRef.current?.focus();
        setDeclarationCheckbox(e.detail.checked);
    };

    const renderAvailableUsers = (user: User) => {
        return (
            <IonSelectOption key={user.id} value={user.id}>
                {user.firstName + " " + user.lastName}
            </IonSelectOption>
        );
    };

    const handlePermitAssigneeAdded = (e: CustomEvent) => {
        let user = availableUsers.find((x) => x.id === e.detail.value);
        setSelectedUser(user);
    };

    const handleChangeButtonClicked = () => {
        setIsOpen(true);
        onRefreshButtonClicked();
    };

    return (
        <>
            <IonToast
                isOpen={showError}
                message={`Please select a permit assignee then read and confirm the declaration by clicking on the checkbox.`}
                position="top"
            />
            <IonButton
                onClick={handleChangeButtonClicked}
                shape="round"
                color="medium"
                size="small"
                style={{ marginLeft: "16px" }}
            >
                <IonIcon slot="start" icon={swapHorizontal} />
                <IonLabel>Change</IonLabel>
            </IonButton>

            <IonModal isOpen={isOpen} onDidDismiss={handleCancel} style={{ "--height": "90%" }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Change Permit Assignee</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={handleCancel} color="medium">
                                Cancel
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                {shouldShowGeofencingError && geofencingError && (
                    <IonItem color="danger">
                        <IonLabel class="ion-text-wrap">{geofencingError}</IonLabel>
                        <IonButton fill="outline" color={"light"} slot="end" onClick={onRefreshButtonClicked}>
                            Try again
                        </IonButton>
                    </IonItem>
                )}

                <IonContent className="ion-padding" style={!abilityToSign ? { "--overflow": "hidden" } : {}}>
                    {!abilityToSign && <IonBackdrop visible={true} style={{ opacity: "0.3" }} stopPropagation={true} />}

                    <IonText color="medium">
                        <p style={{ marginTop: 0 }}>
                            Please select a new permit assignee and sign in the box below. Signature is required.
                        </p>
                    </IonText>

                    <div className="sign-pad-wrapper">
                        <IonSelect
                            style={{ paddingLeft: 16 }}
                            interface="alert"
                            placeholder="Please click to select"
                            onIonChange={(e) => handlePermitAssigneeAdded(e)}
                        >
                            {availableUsers.map(renderAvailableUsers)}
                        </IonSelect>
                        {selectedUser && role && (
                            <>
                                <CustomItem
                                    title="Role"
                                    text={role.name}
                                    tagIcon={call}
                                    tag={selectedUser.telephoneNumber}
                                    lines="none"
                                ></CustomItem>
                                <IonItem lines="none">
                                    <IonLabel class="ion-text-wrap">
                                        {getPermitRoleSettings(role, permitTypeId).authorizationDeclaration}
                                    </IonLabel>
                                    <IonCheckbox
                                        slot="start"
                                        checked={declarationCheckbox}
                                        onIonChange={handelDeclarationCheckbox}
                                    />
                                </IonItem>
                            </>
                        )}
                    </div>

                    <br />

                    <SignaturePad
                        ref={sigPadRef}
                        wrapperRef={signPadWrapperRef}
                        onDone={handleDone}
                        beforeDone={beforeDone}
                    />
                </IonContent>
            </IonModal>
        </>
    );
};

export default ChangePermitAssigneeModal;
